import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Context,
  type ReactNode,
} from 'react';

import { apiGet, apiPost, refreshAccessToken } from '../api';
import { HttpError } from '../apiErrors';
import { unwrapData } from '@wapharma/shared';

import {
  broadcastAuthLogout,
  clearTokens,
  clearTokensIfCurrent,
  getToken,
  setAccessToken,
  subscribeAuthLogout,
} from '../auth';
import { resolveApiMediaUrl } from '../lib/resolveApiMediaUrl';

export type AuthOrganization = {
  id: string;
  slug: string;
  name: string;
  displayName?: string | null;
};

export type AuthUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePictureKey?: string | null;
  profilePictureUrl?: string | null;
  active: boolean;
  roles: string[];
  packs?: string[];
  /** Domain×capacity grants (PHI-215). */
  grants?: string[];
  /** Seed bootstrap account (`ADMIN_SEED_EMAIL`) — configuration initiale eligibility. */
  bootstrapAdmin?: boolean;
  organizations?: AuthOrganization[];
  activeOrgId?: string | null;
};

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  applyPasswordChange: (accessToken: string) => Promise<void>;
  /** `true` if a user session is loaded (or no token and cleared). */
  refreshUser: () => Promise<boolean>;
};

const Ctx: Context<AuthState | null> =
  (import.meta.hot?.data.authCtx as Context<AuthState | null> | undefined) ??
  createContext<AuthState | null>(null);
if (import.meta.hot) import.meta.hot.data.authCtx = Ctx;

type LoginRes = {
  accessToken: string;
  email: string;
  roles: string[];
  mustChangePassword?: boolean;
};

type MeRes = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePictureKey?: string | null;
  profilePictureUrl?: string | null;
  active: boolean;
  roles: string[];
  packs?: string[];
  grants?: string[];
  mustChangePassword?: boolean;
  bootstrapAdmin?: boolean;
  organizations?: AuthOrganization[];
  activeOrgId?: string | null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);
  /** Bumped on login / password-change / logout so stale refreshUser work cannot clobber session. */
  const authEpochRef = useRef(0);

  const refreshUser = useCallback(async (): Promise<boolean> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const epoch = authEpochRef.current;

    const task = (async (): Promise<boolean> => {
      let t = getToken();
      if (!t) {
        // Always try httpOnly refresh cookie — also on /login so a valid session
        // restores after API outage (access token may have been dropped mid-flight).
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          if (epoch !== authEpochRef.current) return false;
          setUser(null);
          setMustChangePassword(false);
          return false;
        }
        t = getToken();
      }

      if (!t) {
        if (epoch !== authEpochRef.current) return false;
        setUser(null);
        setMustChangePassword(false);
        return false;
      }

      const tokenAtStart = t;
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 12_000);

      try {
        const raw = await apiGet<{ data: MeRes | null }>('/auth/me', t, controller.signal);
        if (epoch !== authEpochRef.current) return false;

        const d = unwrapData(raw) as MeRes | null;

        if (!d) {
          clearTokensIfCurrent(tokenAtStart);
          if (epoch !== authEpochRef.current) return false;
          setUser(null);
          setMustChangePassword(false);
          return false;
        }

        setUser({
          id: d.id,
          email: d.email,
          firstName: d.firstName ?? null,
          lastName: d.lastName ?? null,
          profilePictureKey: d.profilePictureKey ?? null,
          profilePictureUrl: resolveApiMediaUrl(d.profilePictureUrl) ?? null,
          active: d.active,
          roles: d.roles,
          packs: d.packs ?? [],
          grants: d.grants ?? [],
          bootstrapAdmin: Boolean(d.bootstrapAdmin),
          organizations: d.organizations ?? [],
          activeOrgId: d.activeOrgId ?? null,
        });
        setMustChangePassword(Boolean(d.mustChangePassword));
        return true;
      } catch (err) {
        // Only wipe session on real auth rejection. Network / timeout / 5xx must
        // keep the access token so refresh after API recovery can succeed on /login.
        const status = err instanceof HttpError ? err.status : null;
        const authRejected = status === 401 || status === 403;
        if (authRejected) {
          clearTokensIfCurrent(tokenAtStart);
          if (epoch !== authEpochRef.current) return false;
          setUser(null);
          setMustChangePassword(false);
          return false;
        }
        // Transient outage: keep existing user (if any) and keep tokens.
        if (epoch !== authEpochRef.current) return false;
        return false;
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();

    refreshPromiseRef.current = task;
    try {
      return await task;
    } finally {
      if (refreshPromiseRef.current === task) {
        refreshPromiseRef.current = null;
      }
      if (epoch === authEpochRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const watchdog = window.setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 15_000);

    void refreshUser().finally(() => {
      cancelled = true;
      window.clearTimeout(watchdog);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(watchdog);
    };
  }, [refreshUser]);

  const beginNewAuthEpoch = useCallback(() => {
    authEpochRef.current += 1;
    refreshPromiseRef.current = null;
  }, []);

  useEffect(() => {
    return subscribeAuthLogout(() => {
      beginNewAuthEpoch();
      clearTokens();
      setUser(null);
      setMustChangePassword(false);
      setLoading(false);
    });
  }, [beginNewAuthEpoch]);

  const login = useCallback(async (email: string, password: string) => {
    const raw = await apiPost<{ data: LoginRes }>('/auth/login', { email, password: password.trim() });
    const d = unwrapData(raw) as LoginRes;
    beginNewAuthEpoch();
    setAccessToken(d.accessToken);
    setMustChangePassword(Boolean(d.mustChangePassword));
    setLoading(true);
    const ok = await refreshUser();
    if (!ok) {
      throw new Error(
        'Session invalide après connexion (GET /auth/me). Vérifiez l’API, CORS, et VITE_API_URL.',
      );
    }
  }, [beginNewAuthEpoch, refreshUser]);

  const applyPasswordChange = useCallback(async (accessToken: string) => {
    beginNewAuthEpoch();
    setAccessToken(accessToken);
    setMustChangePassword(false);
    setLoading(true);
    const ok = await refreshUser();
    if (!ok) {
      throw new Error(
        'Session invalide après changement de mot de passe (GET /auth/me). Rechargez la page ou reconnectez-vous.',
      );
    }
  }, [beginNewAuthEpoch, refreshUser]);

  const logout = useCallback(async () => {
    beginNewAuthEpoch();
    try {
      await apiPost('/auth/logout', {});
    } catch {
      /* ignore */
    }
    clearTokens();
    broadcastAuthLogout();
    setUser(null);
    setMustChangePassword(false);
    setLoading(false);
  }, [beginNewAuthEpoch]);

  const value = useMemo(
    () => ({ user, loading, mustChangePassword, login, logout, applyPasswordChange, refreshUser }),
    [user, loading, mustChangePassword, login, logout, applyPasswordChange, refreshUser],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
